/**
 * PATH: packages/email-hub/src/feature/sender-identity/senderIdentity.ts
 * FILE_ID: EMAILHUB-FEATURE-SENDER-IDENTITY-001
 * PURPOSE: Consume the backend SenderIdentityV1 envelope with legacy fallback.
 * WHY: Reader identity must not re-derive person/company/signal truth per host.
 */

import type { Email } from '../hooks/useEmails'
import type { BackendSchemas } from '@quotech/api'

export type SenderIdentityV1 = BackendSchemas['SenderIdentityV1']

type EmailWithSenderIdentity = Email & {
  sender_identity?: SenderIdentityV1 | null
}

export function senderIdentityFor(
  email: Email,
): SenderIdentityV1 | null {
  const identity = (email as EmailWithSenderIdentity).sender_identity
  return identity?.version === 'sender_identity.v1' ? identity : null
}

/** Feed existing identity components from V1 while old API fields stay additive. */
export function emailWithSenderIdentity(email: Email): Email {
  const identity = senderIdentityFor(email)
  if (!identity) return email
  return {
    ...email,
    from_address: identity.effective_sender.address || email.from_address,
    from_name: identity.effective_sender.name || email.from_name,
    company_label: identity.company.name || email.company_label,
    company_domain: identity.company.domain || email.company_domain,
    client_id: identity.company.id ?? email.client_id,
    contact_id: identity.person.id ?? email.contact_id,
    contact_name: identity.person.name || email.contact_name,
    is_internal_sender: identity.signals.internal,
    teammate_name:
      identity.person.kind === 'teammate'
        ? identity.person.name
        : email.teammate_name,
  }
}

